import { QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { useState } from "react";
import { Link } from "react-router-dom";
import { REACT_APP_ADMIN_LINK } from "../../../utils";
import { DeltaMathSelect } from "../../../shared/DeltaMathSelect";
import BackArrowIcon from "../../../shared/icons/BackArrowIcon";
import {
  ProgressLocalStorageInterface,
  ProgressByStandardData,
  ProgressData,
} from "../ReportTypes";
import { progressFilterOptions } from "../ReportUtil";
import { NoDataMessage } from "../shared/ReportNoDataMessage";
import LoadingSkeloton from "../../utils/LoadingSkeleton";
import { FilterMessage } from "./ProgressFilterMessage";
import { Transition } from "@headlessui/react";
import ProgressLegendDialog from "./ProgressLegendDialog";

export const ProgressReporWrapper = ({
  stateData,
  setStateData,
  isLoading,
  error,
  data,
  selectedInfo,
  children,
}: {
  stateData: ProgressLocalStorageInterface;
  setStateData: React.Dispatch<
    React.SetStateAction<ProgressLocalStorageInterface | null>
  >;
  isLoading: boolean;
  error: Error | null;
  data: ProgressData | ProgressByStandardData | null | undefined;
  selectedInfo: string | number;
  children: React.ReactNode;
}) => {
  const [legendOpen, setLegendOpen] = useState(false);

  const isProgressData =
    data && typeof (data as ProgressData).data?.total === "object";
  const dataExists =
    !data || !data?.data
      ? false
      : isProgressData
      ? Object.keys((data as ProgressData).data).length > 1
      : Object.keys((data as ProgressByStandardData).data.standardToData)
          .length > 0;

  return (
    <>
      {/* Header Block */}
      <div id="report-header" className="border border-b-2 bg-white px-6">
        <Link
          to={`${REACT_APP_ADMIN_LINK}/reports/student-performance/select-standard`}
          className="align-center inline-flex py-4 font-bold"
        >
          <BackArrowIcon classes="mt-1.5" />
          <span className="pl-2 text-dm-brand-blue-500">Back to Standards</span>
        </Link>
        <h2 className="font-serif text-2xl font-bold">
          Student Performance Data
        </h2>
        <div className="flex flex-row justify-between">
          <div className="flex flex-row space-x-2 py-2">
            <span className="border-r-2 pr-2">
              {selectedInfo} {stateData.scope}
              {" Selected"}
            </span>
            <span>{stateData.selectedYear}</span>
          </div>
        </div>

        <div className="flex flex-row items-center pb-8 sm:w-full">
          <div className="w-52 flex-shrink-0">
            <DeltaMathSelect
              label={""}
              options={progressFilterOptions}
              onChangeFn={(
                key: ProgressLocalStorageInterface["assignmentType"]
              ) => {
                setStateData({
                  ...stateData,
                  assignmentType: key,
                });
              }}
              value={stateData.assignmentType || "all"}
            />
          </div>
          <button
            className="relative z-10 ml-2 flex flex-row items-center text-sm"
            onClick={() => {
              setLegendOpen(!legendOpen);
            }}
          >
            <QuestionMarkCircleIcon className="group h-4 w-4 flex-shrink-0 text-gray-400 hover:cursor-pointer" />
            <span className="ml-1 whitespace-nowrap">Help</span>
          </button>
        </div>
      </div>

      {/* Legend Block */}
      <Transition
        as="div"
        show={true}
        appear={true}
        enter="transition-opacity duration-1000"
        enterFrom="transform opacity-0"
        enterTo="transform opacity-100"
        leave="transition-opacity duration-1000"
        leaveFrom="transform opacity-100"
        leaveTo="transform opacity-0"
      >
        <ProgressLegendDialog
          isOpen={legendOpen}
          setIsOpen={setLegendOpen}
          axisOption={stateData.axisOption || "Fluency"}
        />
      </Transition>

      {/* Body Block - isolate error / loading states to simplify report rendering components */}
      <div id="report-body" className="bg-dm-background-blue p-6">
        <div className="rounded-md border bg-white p-6">
          {isLoading ? (
            <LoadingSkeloton />
          ) : error ? (
            <div className="text-red-500">{error.message}</div>
          ) : !dataExists ? (
            <>
              <NoDataMessage />
              {isProgressData && stateData.assignmentType !== "all" && (
                <FilterMessage
                  filterType={
                    stateData.assignmentType === "tests"
                      ? "Tests"
                      : "Pushed Assignments"
                  }
                />
              )}
            </>
          ) : (
            children
          )}
        </div>
      </div>
    </>
  );
};
